/**
 * MOLR — The Collection · conversion page behaviour
 * -------------------------------------------------
 * Loaded as a classic deferred script by sections/molr-collection-page.liquid.
 * Fully self-contained; touches no other file. Everything degrades gracefully:
 * with no JS the product form still submits natively to /cart/add.
 *
 * Responsibilities:
 *   1. Finish switching — swap the pen PNG, page background, ambient glow,
 *      light/dark theme, live price / compare-at / stock, finish name, the
 *      "included" grip finish, and the hidden variant id the form submits.
 *   2. Quantity stepper.
 *   3. Accordions (What's included + FAQ) — max-height expand/collapse.
 *   4. Add to Cart over AJAX (main variant + any toggled upsells in ONE request),
 *      reusing Horizon's cart contract so the drawer / header count morph in
 *      place. Falls back to a native form submit if anything goes wrong, so an
 *      add is never silently lost. The native Shopify payment (Apple Pay / Shop
 *      Pay) button is left to Shopify entirely.
 *   5. Strip the one-time entrance flag once the intro sequence has played.
 *
 * Skipped in the theme editor (design mode re-renders sections and would fight
 * the bound state).
 */
(function () {
  'use strict';

  if (document.documentElement.classList.contains('shopify-design-mode')) return;

  var root = document.querySelector('[data-molr-collection]');
  if (!root || root.dataset.mcBound === '1') return;
  root.dataset.mcBound = '1';

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ── One-time entrance flag ──────────────────────────────────────────────
     The CSS plays the sequence off the server-rendered data-mc-intro flag.
     Strip it once the cascade has finished (~2.7s) so a later finish switch
     never replays the pen scale-in. Reduced motion is handled entirely in CSS. */
  if (root.hasAttribute('data-mc-intro')) {
    window.setTimeout(function () { root.removeAttribute('data-mc-intro'); }, 2700);
  }

  /* ── State from the server ───────────────────────────────────────────────── */
  var dataEl = root.querySelector('[data-mc-data]');
  var data;
  try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }
  var finishes = data.finishes || [];
  if (!finishes.length) return;

  var index = data.activeIndex || 0;
  // Buy-button label, server-rendered so the AJAX handler restores the SAME
  // wording the page shipped with (e.g. "PREORDER") after the "Added ✓" state.
  // Label only — nothing about what is submitted depends on it.
  var ctaText = data.ctaText || 'Add to Cart';

  /* ── Elements ────────────────────────────────────────────────────────────── */
  var pens = root.querySelectorAll('.molr-collection__pen');
  var nodes = Array.prototype.slice.call(root.querySelectorAll('[data-mc-node]'));
  var glow = root.querySelector('.molr-collection__glow');
  var finishNames = root.querySelectorAll('[data-mc-finish-name]');
  var includedFinish = root.querySelector('[data-mc-included-finish]');
  var priceNow = root.querySelector('[data-mc-price-now]');
  var priceWas = root.querySelector('[data-mc-price-was]');
  var stockEl = root.querySelector('[data-mc-stock]');
  var variantInput = root.querySelector('[data-mc-variant-input]');
  var form = root.querySelector('[data-mc-form]') || root.querySelector('form.molr-collection__form');
  var accelerated = root.querySelector('[data-mc-accelerated]');
  var atc = root.querySelector('[data-mc-atc]');
  var atcLabel = root.querySelector('[data-mc-atc-label]');

  /* ── Finish switching ────────────────────────────────────────────────────── */
  function setText(nodeList, text) {
    nodeList.forEach(function (el) { el.textContent = text; });
  }

  /* Buyability of the CURRENT finish. A finish with no matching variant, or a
     sold-out one, must not be addable — but its swatch still switches the
     visual (see selectFinish). Accelerated checkout is hidden alongside, since
     Shopify's payment button would otherwise post a stale/absent variant id. */
  function applyBuyState(f) {
    var buyable = !!(f && f.hasVariant && f.available);
    if (atc) {
      atc.disabled = !buyable;
      atc.setAttribute('aria-disabled', buyable ? 'false' : 'true');
    }
    if (atcLabel) {
      atcLabel.textContent = buyable
        ? ctaText
        : (f && f.hasVariant ? 'Sold out' : 'Unavailable');
    }
    if (accelerated) accelerated.hidden = !buyable;
  }

  function selectFinish(i, focusNode) {
    var f = finishes[i];
    if (!f || i === index) {
      if (focusNode && nodes[i]) nodes[i].focus();
      return;
    }
    index = i;

    // Background + glow + contrast theme.
    root.style.setProperty('--mc-bg', f.bg);
    if (f.glow) root.style.setProperty('--mc-glow', f.glow);
    root.setAttribute('data-theme', f.theme);
    root.setAttribute('data-active', String(i));

    // Pen swap — CSS owns the outgoing (0.25s) / incoming (0.4s) timing.
    pens.forEach(function (pen) {
      var on = parseInt(pen.dataset.penIndex, 10) === i;
      pen.classList.toggle('is-active', on);
      if (on) { pen.removeAttribute('aria-hidden'); }
      else { pen.setAttribute('aria-hidden', 'true'); }
    });

    // Node states + roving tabindex.
    nodes.forEach(function (node, n) {
      var on = n === i;
      node.classList.toggle('is-active', on);
      node.setAttribute('aria-pressed', on ? 'true' : 'false');
      node.setAttribute('tabindex', on ? '0' : '-1');
    });

    // Text: finish name (both columns), included grip finish.
    setText(finishNames, f.name);
    if (includedFinish) includedFinish.textContent = f.name;

    // Live price / compare-at.
    if (priceNow && f.price) priceNow.textContent = f.price;
    if (priceWas) {
      if (f.compareAt) { priceWas.textContent = f.compareAt; priceWas.hidden = false; }
      else { priceWas.hidden = true; }
    }

    // Stock indicator — only when genuinely low (string computed server-side).
    if (stockEl) {
      if (f.stock) { stockEl.textContent = f.stock; stockEl.hidden = false; }
      else { stockEl.textContent = ''; stockEl.hidden = true; }
    }

    // Keep the form submitting the correct variant. The hidden <input name="id">
    // is the single source of truth for BOTH the AJAX add and Shopify's native
    // dynamic checkout button (Shop Pay / Apple Pay / Google Pay), which reads
    // the form's own fields at click time — so updating it here is all that is
    // needed for accelerated checkout to follow the selected finish. The change
    // event is dispatched so any Shopify listener re-syncs off the same value.
    if (variantInput) {
      variantInput.value = f.variantId ? String(f.variantId) : '';
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    applyBuyState(f);

    // Gallery: keep thumbnail 1 picturing the finish just chosen, and bring the
    // pen back to the front — a swatch that changed a pen hidden behind a still
    // would read as a broken control. No-ops when the gallery is switched off.
    if (thumbPen && f.pen) thumbPen.src = f.pen;
    if (thumbs.length) showSlot(1);

    if (focusNode && nodes[i]) nodes[i].focus();
  }

  // Every swatch is bound, including finishes with no matching variant — the
  // finish switch is a VISUAL control first (identical to the hero's swatches).
  // Gating the listener on data-real was the original bug: when the live
  // product's option values didn't match, no listener was attached at all and
  // the whole selector went inert. Buyability is handled by applyBuyState.
  nodes.forEach(function (node, n) {
    node.addEventListener('click', function () { selectFinish(n, false); });
  });

  applyBuyState(finishes[index]);

  // Keyboard: arrow keys move + select across the finish nodes (roving tabindex).
  var nodesWrap = root.querySelector('[data-mc-nodes]');
  if (nodesWrap) {
    nodesWrap.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      var dir = e.key === 'ArrowRight' ? 1 : -1;
      // Step one node, wrapping. Every finish is reachable by keyboard for the
      // same reason every swatch is clickable — see the click binding above.
      selectFinish((index + dir + nodes.length) % nodes.length, true);
    });
  }

  /* ── Quantity stepper ────────────────────────────────────────────────────── */
  var qtyInput = root.querySelector('[data-mc-qty-input]');
  var qtyMinus = root.querySelector('[data-mc-qty-minus]');
  var qtyPlus = root.querySelector('[data-mc-qty-plus]');

  function readQty() {
    var v = parseInt(qtyInput && qtyInput.value, 10);
    if (!v || v < 1) v = 1;
    return v;
  }
  function writeQty(v) { if (qtyInput) qtyInput.value = String(Math.max(1, v)); }

  if (qtyMinus) qtyMinus.addEventListener('click', function () { writeQty(readQty() - 1); });
  if (qtyPlus) qtyPlus.addEventListener('click', function () { writeQty(readQty() + 1); });
  if (qtyInput) qtyInput.addEventListener('change', function () { writeQty(readQty()); });

  /* ── Accordions ──────────────────────────────────────────────────────────── */
  root.querySelectorAll('[data-mc-acc]').forEach(function (head) {
    var panelId = head.getAttribute('aria-controls');
    var panel = panelId ? root.querySelector('#' + (window.CSS && CSS.escape ? CSS.escape(panelId) : panelId)) : null;
    if (!panel) return;
    // Native <button> gives Enter/Space activation for free (brief a11y).
    head.addEventListener('click', function () {
      var open = head.getAttribute('aria-expanded') === 'true';
      head.setAttribute('aria-expanded', open ? 'false' : 'true');
      // Cap to the content height so the max-height transition animates both
      // ways; the panel's own content never changes height enough to clip.
      panel.style.maxHeight = open ? '0px' : panel.scrollHeight + 'px';
    });
  });

  /* ── Three-image gallery ──────────────────────────────────────────────────
     Thumbnail 1 is the pen stage; 2 and 3 are stills layered over it.

     The two systems are kept honest in both directions:
       · picking a thumbnail other than 1 covers the pen with a still;
       · picking thumbnail 1, OR picking any finish swatch, uncovers it again —
         so a swatch can never appear inert because a still is sitting on top
         of the pen it just changed.
     Absent markup (the gallery checkbox off) makes every line here a no-op. */
  var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-mc-thumb]'));
  var extras = Array.prototype.slice.call(root.querySelectorAll('[data-mc-extra]'));
  var thumbPen = root.querySelector('[data-mc-thumb-pen]');

  function showSlot(slot) {
    extras.forEach(function (el) {
      el.hidden = el.dataset.mcExtra !== String(slot);
    });
    thumbs.forEach(function (t) {
      var on = t.dataset.mcThumb === String(slot);
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  thumbs.forEach(function (t) {
    t.addEventListener('click', function () { showSlot(t.dataset.mcThumb); });
  });

  /* ── Kit components — auto-cycle + tap to enlarge ─────────────────────────
     The CSS owns both arrangements (a fan ≥900px, a plain column below) and
     owns desktop hover entirely. This file adds the two things hover cannot
     do, and they work identically in either arrangement because both are
     expressed through the same .is-front class:

       1. AUTO-CYCLE. While the visitor has not interacted, a card is walked
          forward every kitCycleMs. It runs only while the deck is actually on
          screen (IntersectionObserver), only on a coarse pointer — a desktop
          visitor gets hover instead — and never in a backgrounded tab or under
          prefers-reduced-motion.

       2. ENLARGE. A tap opens the component full size in a native <dialog>
          via showModal(): top layer, so it cannot be clipped by the section's
          overflow:hidden or buried under a transformed card; focus trapping,
          Esc-to-close and background inertness come with it. Slots that are
          still placeholders carry data-mc-zoomable="false" and only take the
          front state — there is no photograph to enlarge yet.

     SCROLL SAFETY. Only `click` is bound. No touchstart, no pointerdown, no
     preventDefault, no scroll locking — a tap that turns into a drag scrolls
     the page as normal and the deck never traps the reader. */
  (function kitDeck() {
    var fan = root.querySelector('[data-mc-fan]');
    if (!fan) return;

    var cards = Array.prototype.slice.call(fan.querySelectorAll('[data-mc-card]'));
    if (!cards.length) return;

    var CYCLE_MS = Math.max(2000, parseInt(data.kitCycleMs, 10) || 3000);
    var RESUME_MS = 5000; // quiet period after a tap before the cycle resumes
    var coarse = window.matchMedia('(hover: none), (pointer: coarse)');

    var front = -1;
    var timer = null;
    var resumeTimer = null;
    var onScreen = false;

    /* ── The enlarge dialog ──────────────────────────────────────────────── */
    var zoom = root.querySelector('[data-mc-zoom]');
    var zoomImg = root.querySelector('[data-mc-zoom-img]');
    var zoomCap = root.querySelector('[data-mc-zoom-cap]');
    var zoomClose = root.querySelector('[data-mc-zoom-close]');
    // Feature-detected, not assumed: without showModal the tap simply brings
    // the card forward and nothing breaks.
    var canZoom = !!(zoom && zoomImg && typeof zoom.showModal === 'function');

    function openZoom(btn) {
      if (!canZoom || btn.dataset.mcZoomable !== 'true' || !btn.dataset.mcZoomSrc) return false;
      zoomImg.src = btn.dataset.mcZoomSrc;
      zoomImg.alt = btn.dataset.mcZoomLabel || '';
      if (zoomCap) zoomCap.textContent = btn.dataset.mcZoomLabel || '';
      zoom.showModal();
      return true;
    }

    if (zoomClose && zoom) {
      zoomClose.addEventListener('click', function () { zoom.close(); });
    }
    if (zoom) {
      // Click on the backdrop (i.e. on the dialog box itself, outside the
      // figure) closes, matching the tap-away behaviour of the deck.
      zoom.addEventListener('click', function (e) {
        if (e.target === zoom) zoom.close();
      });
      // Drop the source on close so a backgrounded dialog holds no decoded
      // bitmap, and so re-opening always shows the card that was just tapped.
      zoom.addEventListener('close', function () {
        zoomImg.removeAttribute('src');
      });
    }

    /* ── Front state + cycle ─────────────────────────────────────────────── */
    function paint(i) {
      front = i;
      cards.forEach(function (card, n) {
        var on = n === i;
        card.classList.toggle('is-front', on);
        var btn = card.querySelector('[data-mc-card-btn]');
        if (btn) btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      fan.classList.toggle('has-front', i >= 0);
    }

    function stopCycle() {
      if (timer) { window.clearInterval(timer); timer = null; }
    }

    function startCycle() {
      if (timer || reduce.matches || !coarse.matches || !onScreen) return;
      timer = window.setInterval(function () {
        paint((front + 1) % cards.length);
      }, CYCLE_MS);
    }

    // A tap is an interaction: the cycle stops now and comes back only after a
    // genuine pause, so it never yanks a card away from someone reading it.
    function interacted() {
      stopCycle();
      if (resumeTimer) window.clearTimeout(resumeTimer);
      resumeTimer = window.setTimeout(startCycle, RESUME_MS);
    }

    cards.forEach(function (card, n) {
      var btn = card.querySelector('[data-mc-card-btn]');
      if (!btn) return;
      btn.addEventListener('click', function () {
        interacted();
        if (btn.dataset.mcZoomable === 'true' && canZoom) {
          // Bring the card forward FIRST, so the deck still reads correctly
          // behind the dialog and is left on the right card when it closes.
          paint(n);
          openZoom(btn);
          return;
        }
        // No photograph to enlarge yet: keep the original tap-to-front /
        // tap-again-to-dismiss behaviour rather than leaving a placeholder
        // stuck forward with no way back.
        paint(front === n ? -1 : n);
      });
    });

    // Tap anywhere else dismisses the front state. Bound on the document so it
    // also covers the buy column; the dialog lives in the top layer and is
    // handled by its own listeners above.
    document.addEventListener('click', function (e) {
      if (fan.contains(e.target)) return;
      if (zoom && zoom.contains(e.target)) return;
      if (front < 0) return;
      interacted();
      paint(-1);
    });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        onScreen = entries[0].isIntersecting;
        if (onScreen) startCycle();
        else stopCycle();
      }, { threshold: 0.3 }).observe(fan);
    } else {
      onScreen = true;
      startCycle();
    }

    // A backgrounded tab should not keep swapping cards.
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stopCycle();
      else startCycle();
    });
  })();

  /* ── Add to Cart (AJAX, main + toggled upsells in one request) ───────────── */
  function routes() {
    return (window.Theme && Theme.routes) || {};
  }
  function cartSectionIds() {
    var ids = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach(function (el) {
      ids.push(el.dataset.sectionId);
    });
    return ids.join(',');
  }

  function collectItems() {
    var items = [];
    // Empty when the selected finish has no matching variant — the main line is
    // simply omitted rather than falling back to some other finish's id, so a
    // mismatch can never put the wrong pen in someone's cart.
    var mainId = variantInput && variantInput.value;
    if (mainId) items.push({ id: mainId, quantity: readQty() });
    // The upsell hook is retained but currently matches NOTHING: the MOLR OS
    // row on this page is display-only (it states that MOLR OS is included
    // with the kit) and deliberately carries no data-mc-upsell attribute, so
    // no code path leads from that switch to the cart. Any future row that
    // genuinely should add a line item opts in by carrying the attribute.
    root.querySelectorAll('[data-mc-upsell]').forEach(function (cb) {
      if (cb.checked && !cb.disabled && cb.dataset.variantId) {
        items.push({ id: cb.dataset.variantId, quantity: 1 });
      }
    });
    return items;
  }

  function confirmAdd() {
    if (!atc) return;
    if (atcLabel) atcLabel.textContent = 'Added ✓';
    atc.classList.add('is-added');
    atc.disabled = true;
    window.setTimeout(function () {
      atc.classList.remove('is-added');
      // Restore from the CURRENT finish rather than a captured label, so the
      // button can't come back enabled on a finish that isn't buyable.
      applyBuyState(finishes[index]);
    }, 1500);
  }

  async function ajaxAdd(items) {
    // Optimistic Horizon event so the drawer/header can react before the round
    // trip; wrapped so a missing @shopify/events module never breaks the add.
    var CartLinesUpdateEvent = null;
    var deferred = null;
    try {
      var mod = await import('@shopify/events');
      CartLinesUpdateEvent = mod && mod.CartLinesUpdateEvent;
    } catch (e) { /* progressive: fall back to a plain add + confirmation */ }

    if (CartLinesUpdateEvent) {
      deferred = CartLinesUpdateEvent.createPromise();
      document.dispatchEvent(new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map(function (l) { return { merchandiseId: l.id, quantity: l.quantity }; }),
        promise: deferred.promise,
      }));
    }

    try {
      var addUrl = routes().cart_add_url || '/cart/add.js';
      var res = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items, sections: cartSectionIds(), sections_url: window.location.pathname }),
      });
      var added = await res.json();
      if (added.status) {
        if (deferred) deferred.reject(added);
        throw new Error(added.description || added.message || 'Add to cart failed');
      }

      if (CartLinesUpdateEvent && deferred) {
        var cartUrl = (routes().cart_url || '/cart') + '.js';
        var cart = await (await fetch(cartUrl, { headers: { Accept: 'application/json' } })).json();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { items: cart.items, source: 'molr-collection', sections: added.sections, didError: false },
        });
      }
      return true;
    } catch (error) {
      if (deferred) deferred.reject(error);
      throw error;
    }
  }

  if (form) {
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      // No buyable variant for the selected finish (or the form is empty) —
      // don't post, or Shopify answers with a cart error page. The button is
      // already disabled in this state; this also covers Enter-in-the-quantity.
      if (!variantInput || !variantInput.value) return;
      var items = collectItems();
      if (!items.length) return;
      if (atc) atc.disabled = true;
      ajaxAdd(items)
        .then(function () { confirmAdd(); })
        .catch(function () {
          // Honour the add: let the real form post so the item is never lost.
          if (atc) atc.disabled = false;
          form.submit();
        });
    });
  }
})();
